import React, { useEffect, useState } from "react"
import { Link, useNavigate, useParams } from 'react-router'
import { useTranslation } from 'react-i18next'

import DocumentRevisions from 'pages/parts/DocumentRevisions'
import { httpOk, GET } from "helpers/api"
import { formatMoney, formatDate } from 'helpers/helpers'

import ThermalPrint, { ThermalPrintButton, useThermalPrint } from 'pages/parts/ThermalPrint'

function Preview() {
	const { t } = useTranslation()
	const { id } = useParams()
	const navigate = useNavigate()

	const [data, setData] = useState({})
	const [revisionsDocumentId, setRevisionsDocumentId] = useState(null)
	const { printRef, handlePrint } = useThermalPrint()

	async function getData() {
		const response = await GET(`/documents-return/${id}`, {}, { loader: true })
		if (httpOk(response)) setData(response.data)
	}

	useEffect(() => {
		getData()
	}, []) // eslint-disable-line react-hooks/exhaustive-deps

	return (
		<>
			<div className="page-header d-flex justify-content-between">
				<div className="d-flex text-truncate">
					<h4 className="vertical-center">
						{t('return_supplier')}
					</h4>
					<div className="vertical-center mt-1">
						<i className="uil uil-angle-double-right fz-20"></i>
					</div>
					<div className="vertical-center mt-1">
						<h6>{t('preview')}</h6>
					</div>
				</div>

				<div className="d-flex align-items-center gap-2">
					<button type="button" className="btn btn-outline-primary btn-rounded"
						onClick={() => setRevisionsDocumentId(id)}>
						<i className="uil-history me-1"></i>
						{t('document_revisions')}
					</button>
					{!!data.editable &&
						<Link to={`/documents-return/update/${id}`} className="btn btn-primary btn-rounded">
							<i className="uil-edit-alt me-1"></i>
							{t('edit')}
						</Link>
					}
					<ThermalPrintButton onClick={handlePrint} />
				</div>
			</div>

			<div className="card mb-3">
				<div className="card-body">
					<h4 className="fw-600">{t('document_from')} {formatDate(data.created_at, 'dd.MM.yy HH:mm')}</h4>
					<div className="flex-column preview-info">
						<div className="d-flex gap-2">
							<div className="fw-600">{t('pos')}:</div>
							<div>{data.pos_name}</div>
						</div>
						<div className="d-flex gap-2">
							<div className="fw-600">{t('supplier')}:</div>
							<div>{data.supplier_name}</div>
						</div>
						{!!data.wms_transfer_pos_id &&
							<div className="d-flex gap-2">
								<div className="fw-600">{t('return_basis_transfer')}:</div>
								<div>№{data.wms_transfer_pos_id}</div>
							</div>
						}
						{data.integration_status !== null && data.integration_status !== undefined &&
							<div className="d-flex gap-2">
								<div className="fw-600">{t('supplier_decision')}:</div>
								<div className={Number(data.integration_status) === 1 ? 'text-success' : (Number(data.integration_status) === -1 ? 'text-danger' : 'text-warning')}>
									{Number(data.integration_status) === -2 && t('integration_return_pending')}
									{Number(data.integration_status) === 1 && t('integration_return_accepted')}
									{Number(data.integration_status) === -1 && `${t('integration_return_rejected')}: ${data.integration_reject_reason ?? ''}`}
								</div>
							</div>
						}
						<div className="d-flex gap-2">
							<div className="fw-600">{t('document')}:</div>
							<div>{data.document_number}</div>
						</div>
						<div className="d-flex gap-2">
							<div className="fw-600">{t('note')}:</div>
							<div>{data.note}</div>
						</div>
					</div>
				</div>
			</div>

			<div className="card">
				<div className="card-body">
					<div className="table-responsive">
						<table className="table table-bordered">
							<thead>
								<tr>
									<th style={{ 'minWidth': '33px' }} className="text-center">#</th>
									<th style={{ 'width': '40%' }}>{t('name2')}</th>
									<th style={{ 'width': '10%' }}>{t('packaging')}</th>
									<th style={{ 'width': '10%' }} className="text-end">{t('quantity')}</th>
									<th style={{ 'width': '10%' }} className="text-end">{t('price')}</th>
									<th style={{ 'width': '10%' }} className="text-end">{t('fact')}</th>
									<th style={{ 'width': '10%' }} className="text-start">{t('unit')}</th>
									<th style={{ 'width': '10%' }} className="text-end">{t('amount')}</th>
									<th style={{ 'minWidth': '33px' }}></th>
								</tr>
							</thead>
							<tbody>
								{data?.items?.map((item, index) => (
									<tr key={index}>
										<td className="text-center" title={item.id}>
											{index + 1}
										</td>
										<td title={item.product_id}>
											{item.product_name}
										</td>
										<td>{item.packaging_name}</td>
										<td className="text-end">{formatMoney(item.packaging_quantity)}</td>
										<td className="text-end">{formatMoney(item.price)}</td>
										<td className="text-end">{item.quantity}</td>
										<td className="text-start">{item.unit_name}</td>
										<td className="text-end">{formatMoney(item.price * item.quantity)}</td>
										<td></td>
									</tr>
								))}
								<tr>
									<td></td>
									<td><b>{t('total')}</b></td>
									<td></td>
									<td></td>
									<td></td>
									<td className="text-end text-nowrap">
										<b>{formatMoney(data?.overall?.overall_quantity)}</b>
									</td>
									<td></td>
									<td className="text-end text-nowrap">
										<b>{formatMoney(data?.overall?.overall_amount)}</b>
									</td>
									<td></td>
								</tr>
							</tbody>
						</table>
					</div>

					<div className="d-flex justify-content-end mt-5">
						<button type="button" className="btn btn-warning btn-rounded btn-wide"
							onClick={() => navigate(-1)}>
							{t('cancel')}
						</button>
					</div>
				</div>
			</div>

			<ThermalPrint
				printRef={printRef}
				title={`${t('document_from')} ${formatDate(data.created_at, 'dd.MM.yy HH:mm')}`}
				info={[
					{ label: t('pos'), value: data.pos_name },
					{ label: t('supplier'), value: data.supplier_name },
					{ label: t('document'), value: data.document_number },
					{ label: t('note'), value: data.note },
				]}
				columns={[
					{ label: t('name2'), width: '42%', numbered: true, render: (item) => item.product_name },
					{ label: t('quantity'), width: '16%', align: 'end', render: (item) => formatMoney(item.quantity, 2) },
					{ label: t('price'), width: '20%', align: 'end', render: (item) => formatMoney(item.price) },
					{ label: t('amount'), width: '22%', align: 'end', render: (item) => formatMoney(item.price * item.quantity) },
				]}
				rows={data?.items || []}
				total={[t('total'), formatMoney(data?.overall?.overall_quantity), '', formatMoney(data?.overall?.overall_amount)]}
			/>

			<DocumentRevisions
				entity="documents_return"
				documentId={revisionsDocumentId}
				setDocumentId={setRevisionsDocumentId}>
			</DocumentRevisions>
		</>
	)
}

export default Preview